import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../stores';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { statusOptions } from '../../../constant';
import AddProductDialog from './AddProductDialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import PrintDelieveryNoteButton from '../../../components/PrintableDelieveryNote';

// import EditOrderDialog from './EditOrderDialog';

const maxDescriptionLength: number = 40;

const nameBodyTemplate = (product: any) => {
    const shortenedDescription =
        (product.description || '').length > maxDescriptionLength
            ? product.description.substring(0, maxDescriptionLength) + '...'
            : product.description || '-';


    return <>
        <span className={'text-xs white-space-normal font-bold'}>{product.name}</span><br />
        <span className={'text-xs'}>{shortenedDescription}</span>
    </>
};
const imageBodyTemplate = (product: any) => {
    return product.image ? <img
        className="sm:w-16rem xl:w-10rem shadow-2 block xl:block mx-auto border-round-xl"
        // className="w-9 sm:w-16rem xl:w-10rem shadow-2 block xl:block mx-auto border-round-md"
        src={product.image}
        alt={product.name}
        style={{ maxWidth: 64, maxHeight: 64 }}
    /> : <i className="pi pi-image" style={{ fontSize: '2rem' }}></i>;
};

const BodyCounterTemplate = observer(({ product, store }: { product: any, store: any }) => {
    const currentCount = product?.units_after || 0; // Alte Zahl (Standardwert 0, falls nicht vorhanden)
    const oldCount = product?.units || 0; // Alte Zahl (Standardwert 0, falls nicht vorhanden)

    return (
        <div className="flex gap-1 align-items-center">
            {/* Minus-Button */}
            <Button
                icon="pi pi-minus"
                style={{
                    maxWidth: '32px',
                    maxHeight: '30px',
                }}
                disabled={!store.isEditable}
                className="p-button-outlined p-button-rounded"
                onClick={() => store.updateProductUnits(product.id, -1)}
            ></Button>

            {/* Aktuelle und alte Zahl */}
            <div className="flex flex-column align-items-center">
                {currentCount === oldCount ? (
                    <span className="flex align-items-center justify-content-center w-2rem h-2rem bg-primary font-bold border-round text-sm">
                        {currentCount}
                    </span>
                ) : (
                    <>
                        <span className="flex align-items-center justify-content-center w-2rem h-2rem bg-primary font-bold border-round text-sm">
                            {currentCount}
                        </span>
                        <span className="text-xs text-secondary" style={{ fontStyle: 'italic', color: '#ff6b6b' }}>
                            {oldCount}
                        </span>
                    </>
                )}
            </div>

            {/* Plus-Button */}
            <Button
                icon="pi pi-plus"
                style={{
                    maxWidth: '32px',
                    maxHeight: '30px',
                }}
                disabled={!store.isEditable}
                className="p-button-outlined p-button-rounded p-button-success"
                onClick={() => store.updateProductUnits(product.id, 1)}
            ></Button>
        </div>
    );
});

const DialogHeader = ({ text, objectName, onEdit }: any) => {
    return (
        <div className="inline-flex align-items-center justify-content-center gap-2">
            <span>{text} - </span>
            <span
                className="clickable-object"
                onClick={onEdit} // Trigger the edit action
                style={{ cursor: 'pointer', color: '#E6007E', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
                {objectName}
                <i
                    className="pi pi-pencil"
                    style={{ fontSize: '1rem', color: '#E6007E' }}
                ></i>
            </span>
        </div>
    );
};

const DialogFooter = observer(({ onSave, onArchived, store }: any) => {
    return <div className="flex justify-content-between align-items-center">
        {/* Produkt hinzufügen Button */}
        <Button
            label="Produkt hinzufügen"
            icon="pi pi-plus"
            disabled={!store.isEditable}
            onClick={() => store.openAddProductDialog()} // Öffnet das Produkt-Auswahl-Dropdown
        />

        {/* Speichern Button */}
        <div>
            <PrintDelieveryNoteButton store={store} onArchived={() => onArchived()} />
            <Button
                label="Speichern + Schließen"
                icon="pi pi-save"
                onClick={onSave}
                disabled={!store.hasChanged} // Aktiviert nur, wenn Änderungen vorliegen
                autoFocus
            />
        </div>
    </div>
});

const OrderDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog || null;
    const useFreeProducts = store?.freeProducts || [];
    const useChargeableProducts = store?.chargeableProducts || [];
    return <Dialog
        visible={!!store}
        modal
        header={() => <DialogHeader
            text={`Bestellung #${store.orderId}`}
            objectName={store.objectName}
            onEdit={() => store.editCleanerOrObject()}
        />}
        footer={() => <DialogFooter onSave={async () => {
            await store.onSaveOrder();
            sdk.closeDialog({
                event: 'SAVED'
            });
        }} onArchived={async () => {
            sdk.closeDialog({
                event: 'SAVED'
            });
        }} store={store} />}
        style={{ width: '75rem' }}
        onHide={() => sdk.closeDialog()}>
        {store.orderLoading ? (
            <div className="flex justify-content-center align-items-center" style={{ height: '100%' }}>
                <ProgressSpinner />
            </div>
        ) : (
            <>
                <div className="flex justify-content-between align-items-center cleaner-name border-round-sm p-3">
                    <div style={{ textAlign: 'left' }}>
                        <span style={{ fontWeight: 'bold' }}>Bestellung von:</span>
                        <span
                            style={{
                                // textDecoration: 'underline', 
                                color: '#E6007E',
                                marginLeft: 8,
                                cursor: 'pointer',
                                fontStyle: 'italic'
                            }}
                            onClick={() => store.editCleanerOrObject()}
                        >
                            {store.cleanerName}
                            <i className="pi pi-pencil" style={{ fontSize: '1rem', color: '#E6007E', marginLeft: 8 }}></i>
                        </span>
                    </div>
                </div>
                <div className="grid">
                    <div className="col">
                        <div className="text-center p-3 border-round-sm bg-primary font-bold">
                            <h4 className="table-title">Inklusive</h4>
                            <DataTable
                                value={[...useFreeProducts].sort((a: any, b: any) => a.name.localeCompare(b.name))}
                                size={'small'}
                                showHeaders={false}
                                emptyMessage={'-'}
                            >
                                <Column header="Image" style={{ textAlign: 'center' }} body={imageBodyTemplate}></Column>
                                <Column field="name" header="Name" body={nameBodyTemplate}></Column>
                                <Column body={(p: any) => (<BodyCounterTemplate product={p} store={store} />)}></Column>
                            </DataTable>
                        </div>
                    </div>
                    <div className="col">
                        <div className="text-center p-3 border-round-sm bg-primary font-bold">
                            <h4 className="table-title">Kostenpflichtig</h4>
                            <DataTable
                                value={[...useChargeableProducts].sort((a: any, b: any) => a.name.localeCompare(b.name))}
                                size={'small'}
                                showHeaders={false}
                                emptyMessage={'-'}
                            >
                                <Column header="Image" style={{ textAlign: 'center' }} body={imageBodyTemplate}></Column>
                                <Column field="name" header="Name" body={nameBodyTemplate}></Column>
                                <Column body={(p: any) => (<BodyCounterTemplate product={p} store={store} />)}></Column>
                            </DataTable>
                        </div>
                    </div>
                    <div className="col" style={{ minWidth: 320 }}>
                        <div className="text-center p-3 border-round-sm bg-primary font-bold ">
                            <h4 className="table-title">Kommentar</h4>
                            <InputTextarea
                                value={store.comment}
                                onChange={(e) => store.updateComment(e.target.value)}
                                rows={5}
                                cols={30}
                                disabled={!store.isEditable}
                                style={{ width: "100%", marginBottom: "10px" }}
                                placeholder="Kein Kommentar"
                            />
                            {/* Dropdown für Status */}
                            <h4 className="table-title">Status</h4>
                            <Dropdown
                                value={store.status}
                                options={statusOptions}
                                onChange={(e) => store.updateStatus(e.value)}
                                placeholder="Status auswählen"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                </div>
            </>
        )}
        <AddProductDialog store={store} />
        {/* <EditOrderDialog store={store} /> */}
    </Dialog>
});

export default OrderDialog;